<template>
  <div ref="chartContainer" class="h-64 w-full"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import * as echarts from 'echarts'

interface SeriesConfig {
  name: string
  key: string
  color: string
}

const props = defineProps<{
  title: string
  data: Array<Record<string, any>>
  series: SeriesConfig[]
  unit?: string
}>()

const chartContainer = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null

const initChart = async () => {
  if (!chartContainer.value) return

  await nextTick()
  
  chartInstance = echarts.init(chartContainer.value)
  updateChart()
}

const updateChart = () => {
  if (!chartInstance || !props.data.length) return

  const times = props.data.map(d => d.time)
  
  const seriesData = props.series.map(series => ({
    name: series.name,
    type: 'line',
    smooth: true,
    data: props.data.map(d => d[series.key] ?? null),
    itemStyle: {
      color: series.color
    },
    lineStyle: {
      width: 2
    },
    symbol: 'circle',
    symbolSize: 4,
    connectNulls: false
  }))

  const option: echarts.EChartsOption = {
    title: {
      text: props.title,
      left: 'center',
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: props.series.map(s => s.name),
      bottom: 0,
      itemWidth: 10,
      itemHeight: 8,
      textStyle: {
        fontSize: 10
      }
    },
    grid: {
      left: '8%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: times,
      axisLabel: {
        rotate: 30,
        fontSize: 9,
        interval: 'auto'
      }
    },
    yAxis: {
      type: 'value',
      name: props.unit || '',
      nameLocation: 'middle',
      nameGap: 40
    },
    series: seriesData
  }
  
  chartInstance.setOption(option, true)
}

watch(() => props.data, () => {
  updateChart()
}, { deep: true })

onMounted(() => {
  initChart()
  
  // Redimensiona o gráfico quando a janela mudar de tamanho
  const handleResize = () => {
    chartInstance?.resize()
  }
  window.addEventListener('resize', handleResize)
  
  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
  })
})

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>
